import React, { useEffect, useState, useCallback } from 'react';
import { Buildings, Plus, PencilSimple, Trash, ArrowRight, CheckCircle, Globe } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { listCompanies, createCompany, updateCompany, deleteCompany, switchActiveCompany } from '../../services/api';

/**
 * Master-admin-only page to manage all tenant companies on the platform.
 *
 * Lets the developer/system owner:
 *   - See every company, with quick stats (users / orders / clients)
 *   - Create a brand new tenant from scratch (empty data, same feature set)
 *   - Edit the existing 360 Carexport profile (brand, integrations)
 *   - Switch the "active company" context — all admin pages then operate on
 *     that company's data.
 */
const CompaniesPage = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const blank = {
    name: '',
    slug: '',
    address: '',
    phone: '',
    email: '',
    brand: { brand_name: '', brand_short: '', brand_tagline: '', primary_color: '#0c1a2e', logo_url: '' },
    integrations: {
      resend_api_key: '', resend_from_email: '', terminal49_api_key: '',
      telegram_bot_token: '', telegram_admin_chat_id: '',
      zapier_orders_webhook: '', zapier_cars_webhook: '',
      zapier_clients_webhook: '', zapier_payments_webhook: '',
    },
    first_admin_email: '', first_admin_password: '', first_admin_name: '',
  };
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listCompanies();
      setCompanies(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load companies');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(blank);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      ...blank,
      ...c,
      brand: { ...blank.brand, ...(c.brand || {}) },
      integrations: { ...blank.integrations, ...(c.integrations || {}) },
      // first_admin only valid on create — ignore on edit
      first_admin_email: '', first_admin_password: '', first_admin_name: '',
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Company name is required');
    if (!form.slug.trim()) return toast.error('Slug is required (used for subdomain)');
    try {
      if (editing) {
        await updateCompany(editing.id, {
          name: form.name, slug: form.slug, address: form.address,
          phone: form.phone, email: form.email,
          brand: form.brand, integrations: form.integrations,
        });
        toast.success('Company updated');
      } else {
        await createCompany(form);
        toast.success(`Company "${form.name}" created. ${form.first_admin_email ? 'Admin login issued.' : ''}`);
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (c) => {
    if (c.is_default) return toast.error('Cannot delete the default company');
    if (!window.confirm(`Soft-delete ${c.name}? Its data stays in the DB but the company is marked inactive.`)) return;
    try {
      await deleteCompany(c.id);
      toast.success('Company deactivated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const switchTo = async (c) => {
    try {
      await switchActiveCompany(c ? c.id : 'all');
      // Clear cached system info so branding refreshes on next page load.
      localStorage.setItem('active_company_id', c ? c.id : 'all');
      toast.success(c ? `Switched to ${c.name}` : 'Switched to ALL companies view');
      setTimeout(() => window.location.reload(), 300);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Switch failed');
    }
  };

  const activeId = localStorage.getItem('active_company_id') || 'all';

  if (user?.role !== 'master_admin') {
    return (
      <div className="text-center py-20 text-muted-foreground" data-testid="companies-forbidden">
        This area is restricted to the Master Admin.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Buildings className="w-7 h-7" /> Companies
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Each company is a fully isolated tenant — its own brand, integrations, users and data.
            New companies start empty. The default <strong>360 Carexport</strong> entry holds every existing record.
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-company-btn">
          <Plus className="w-4 h-4 mr-2" /> New Company
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full text-center py-12 text-muted-foreground">Loading…</div>
        ) : companies.length === 0 ? (
          <div className="col-span-full text-center py-12 text-muted-foreground">No companies yet.</div>
        ) : (
          companies.map((c) => {
            const isActive = activeId === c.id;
            return (
              <div
                key={c.id}
                className={`border rounded-lg p-4 bg-white relative ${isActive ? 'ring-2 ring-primary border-primary' : 'border-border'}`}
                data-testid={`company-card-${c.slug}`}
              >
                {isActive && (
                  <span className="absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary text-primary-foreground flex items-center gap-1">
                    <CheckCircle weight="fill" className="w-3 h-3" /> ACTIVE
                  </span>
                )}
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center text-lg font-bold text-slate-700 flex-shrink-0">
                    {(c.brand?.brand_short || c.name || '?').substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Globe className="w-3 h-3" /> {c.slug}.carexport360.net
                    </div>
                    {c.is_default && (
                      <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 mt-1">
                        DEFAULT
                      </span>
                    )}
                    {!c.is_active && (
                      <span className="inline-block ml-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-800 mt-1">
                        INACTIVE
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div className="bg-slate-50 rounded p-2">
                    <div className="text-xs text-muted-foreground">Users</div>
                    <div className="font-bold">{c.user_count ?? 0}</div>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <div className="text-xs text-muted-foreground">Orders</div>
                    <div className="font-bold">{c.order_count ?? 0}</div>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <div className="text-xs text-muted-foreground">Clients</div>
                    <div className="font-bold">{c.client_count ?? 0}</div>
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant={isActive ? 'secondary' : 'default'} onClick={() => switchTo(isActive ? null : c)} className="flex-1" data-testid={`switch-${c.slug}`}>
                    {isActive ? 'Exit' : <><ArrowRight className="w-4 h-4 mr-1" /> Enter</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-${c.slug}`}>
                    <PencilSimple className="w-4 h-4" />
                  </Button>
                  {!c.is_default && (
                    <Button size="sm" variant="outline" onClick={() => remove(c)} data-testid={`del-${c.slug}`}>
                      <Trash className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'New Company'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-3">
            {/* ===== Basics ===== */}
            <section>
              <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-slate-600">Company basics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Company name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name" />
                </div>
                <div className="space-y-1">
                  <Label>Slug * <span className="text-[10px] text-muted-foreground">used for subdomain</span></Label>
                  <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} data-testid="company-slug" />
                </div>
                <div className="space-y-1">
                  <Label>Address</Label>
                  <Input value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                <div className="space-y-1">
                  <Label>Phone</Label>
                  <Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label>Email</Label>
                  <Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
              </div>
            </section>

            {/* ===== Brand ===== */}
            <section>
              <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-slate-600">Brand identity</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Brand display name</Label>
                  <Input value={form.brand.brand_name || ''} onChange={(e) => setForm({ ...form, brand: { ...form.brand, brand_name: e.target.value } })} />
                </div>
                <div className="space-y-1">
                  <Label>Short name <span className="text-[10px] text-muted-foreground">for sidebar</span></Label>
                  <Input value={form.brand.brand_short || ''} onChange={(e) => setForm({ ...form, brand: { ...form.brand, brand_short: e.target.value } })} />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label>Tagline</Label>
                  <Textarea rows={2} value={form.brand.brand_tagline || ''} onChange={(e) => setForm({ ...form, brand: { ...form.brand, brand_tagline: e.target.value } })} />
                </div>
                <div className="space-y-1">
                  <Label>Logo URL</Label>
                  <Input value={form.brand.logo_url || ''} onChange={(e) => setForm({ ...form, brand: { ...form.brand, logo_url: e.target.value } })} placeholder="https://…/logo.png or /logo.png" />
                </div>
                <div className="space-y-1">
                  <Label>Primary color</Label>
                  <Input type="color" value={form.brand.primary_color || '#0c1a2e'} onChange={(e) => setForm({ ...form, brand: { ...form.brand, primary_color: e.target.value } })} />
                </div>
              </div>
            </section>

            {/* ===== Integrations ===== */}
            <section>
              <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-slate-600">Integrations (per-company)</h3>
              <p className="text-xs text-muted-foreground mb-3">Each company brings its own API credentials. Leave blank to skip the integration.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Resend API key</Label>
                  <Input value={form.integrations.resend_api_key || ''} onChange={(e) => setForm({ ...form, integrations: { ...form.integrations, resend_api_key: e.target.value } })} />
                </div>
                <div className="space-y-1">
                  <Label>Resend From email</Label>
                  <Input value={form.integrations.resend_from_email || ''} onChange={(e) => setForm({ ...form, integrations: { ...form.integrations, resend_from_email: e.target.value } })} placeholder="noreply@yourdomain.com" />
                </div>
                <div className="space-y-1">
                  <Label>Terminal49 API key</Label>
                  <Input value={form.integrations.terminal49_api_key || ''} onChange={(e) => setForm({ ...form, integrations: { ...form.integrations, terminal49_api_key: e.target.value } })} />
                </div>
                <div className="space-y-1">
                  <Label>Telegram Bot token</Label>
                  <Input value={form.integrations.telegram_bot_token || ''} onChange={(e) => setForm({ ...form, integrations: { ...form.integrations, telegram_bot_token: e.target.value } })} />
                </div>
                <div className="space-y-1">
                  <Label>Telegram Admin chat ID</Label>
                  <Input value={form.integrations.telegram_admin_chat_id || ''} onChange={(e) => setForm({ ...form, integrations: { ...form.integrations, telegram_admin_chat_id: e.target.value } })} />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label>Zapier — orders webhook</Label>
                  <Input value={form.integrations.zapier_orders_webhook || ''} onChange={(e) => setForm({ ...form, integrations: { ...form.integrations, zapier_orders_webhook: e.target.value } })} />
                </div>
              </div>
            </section>

            {/* ===== First admin (create flow only) ===== */}
            {!editing && (
              <section>
                <h3 className="font-semibold mb-3 text-sm uppercase tracking-wide text-slate-600">First admin user <span className="text-[10px] text-muted-foreground normal-case font-normal">(optional)</span></h3>
                <p className="text-xs text-muted-foreground mb-3">Bootstrap the new company with its first administrator login. Without this, you'll need to add users manually after creation.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label>Email</Label>
                    <Input type="email" value={form.first_admin_email} onChange={(e) => setForm({ ...form, first_admin_email: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>Password</Label>
                    <Input type="password" value={form.first_admin_password} onChange={(e) => setForm({ ...form, first_admin_password: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label>Display name</Label>
                    <Input value={form.first_admin_name} onChange={(e) => setForm({ ...form, first_admin_name: e.target.value })} />
                  </div>
                </div>
              </section>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-company-btn">{editing ? 'Save changes' : 'Create company'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompaniesPage;
